import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Home, Bed, Bath, Maximize, MapPin, X, Check, Minus, Search } from 'lucide-react';
import { useCompare } from '@/hooks/useCompare';

function formatUSD(n) {
  if (typeof n !== 'number' || Number.isNaN(n)) return '—';
  return `$${Math.round(n).toLocaleString()}`;
}

function bestDiscount(prop) {
  const dd = prop?.duration_discounts;
  if (!dd || typeof dd !== 'object') return 0;
  const vals = Object.values(dd).map((v) => Number(v) || 0);
  if (vals.length === 0) return 0;
  return Math.max(...vals);
}

/* eslint-disable react/prop-types */
function ComparisonRow({ label, values, formatter, highlightMax, highlightMin, ids }) {
  const numericVals = values.map((v) => (typeof v === 'number' ? v : null)).filter((v) => v != null);
  const target = highlightMax ? Math.max(...numericVals) : highlightMin ? Math.min(...numericVals) : null;
  return (
    <div
      className="grid gap-2 sm:gap-3 py-3 border-b border-border/60 last:border-none"
      style={{ gridTemplateColumns: `clamp(96px, 28vw, 160px) repeat(${values.length}, minmax(120px, 1fr))` }}
    >
      <div className="text-xs sm:text-sm font-medium text-muted-foreground pt-1">{label}</div>
      {values.map((v, i) => {
        const highlighted = target != null && v === target;
        return (
          <div key={ids?.[i] ?? `col-${i}`}
               data-testid={`compare-cell-${label.toLowerCase().replace(/\s+/g, '-')}-${i}`}
               className={`text-xs sm:text-sm break-words ${highlighted ? 'font-semibold text-primary' : ''}`}>
            {formatter ? formatter(v) : (v ?? <Minus className="w-3 h-3 inline text-muted-foreground" />)}
          </div>
        );
      })}
    </div>
  );
}
/* eslint-enable react/prop-types */

export default function ComparePage() {
  const navigate = useNavigate();
  const { ids, remove, clear } = useCompare();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      if (ids.length === 0) {
        setItems([]);
        setLoading(false);
        return;
      }
      try {
        const results = await Promise.all(
          ids.map((id) => axios.get(`${API}/properties/${id}`).then((r) => r.data).catch(() => null))
        );
        if (!cancelled) setItems(results.filter(Boolean));
      } catch (_e) {
        if (!cancelled) toast.error('Failed to load one or more properties');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [ids]);

  const cols = items.length;

  return (
    <div className="min-h-screen bg-background" data-testid="compare-page">
      <header className="glass-header bg-white/70 border-b border-border/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button data-testid="compare-back-btn" onClick={() => navigate(-1)} variant="outline" size="sm" className="rounded-full shrink-0">
              <ArrowLeft className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Back</span>
            </Button>
            <div className="flex items-center gap-2 min-w-0">
              <Home className="w-6 h-6 text-primary shrink-0" />
              <span className="text-lg sm:text-xl font-bold tracking-tight text-primary truncate">PrimePocket</span>
            </div>
          </div>
          {items.length > 0 && (
            <Button data-testid="compare-clear-btn" onClick={clear} variant="ghost" size="sm" className="shrink-0">
              Clear all
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-10 lg:py-12">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight">Compare Properties</h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">Side-by-side breakdown of up to 3 properties you're considering.</p>
        </div>

        {loading ? (
          <div className="text-center py-12 sm:py-20 text-muted-foreground">Loading properties…</div>
        ) : items.length === 0 ? (
          <Card className="p-8 sm:p-16 text-center border-2 border-dashed" data-testid="compare-empty">
            <Search className="w-12 h-12 sm:w-14 sm:h-14 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg sm:text-xl font-medium mb-2">Nothing to compare yet</h3>
            <p className="text-muted-foreground mb-6 text-sm sm:text-base">Add properties from the search page using the &quot;Compare&quot; button.</p>
            <Button data-testid="compare-browse-btn" onClick={() => navigate('/')} className="rounded-full">
              Browse properties
            </Button>
          </Card>
        ) : (
          <Card className="p-3 sm:p-6 border border-border overflow-x-auto">
            {/* Header row */}
            <div
              className="grid gap-2 sm:gap-3 pb-4 border-b border-border"
              style={{ gridTemplateColumns: `clamp(96px, 28vw, 160px) repeat(${cols}, minmax(120px, 1fr))` }}
            >
              <div className="text-xs sm:text-sm font-medium text-muted-foreground">Property</div>
              {items.map((p, i) => (
                <div key={p.id} className="relative pr-6" data-testid={`compare-header-${i}`}>
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt={p.title} className="w-full h-20 sm:h-28 object-cover rounded-md mb-2" />
                  ) : (
                    <div className="w-full h-20 sm:h-28 bg-muted rounded-md mb-2" />
                  )}
                  <div className="font-medium text-xs sm:text-sm leading-snug line-clamp-2">{p.title}</div>
                  <button
                    data-testid={`compare-remove-${i}`}
                    onClick={() => remove(p.id)}
                    className="absolute top-1 right-1 p-1 rounded-full bg-white/90 border border-border hover:bg-muted"
                    aria-label={`Remove ${p.title} from comparison`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>

            <ComparisonRow label="City" values={items.map((p) => p.city)} ids={items.map((p) => p.id)} />
            <ComparisonRow label="Address" values={items.map((p) => p.address)} ids={items.map((p) => p.id)} formatter={(v) => (
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{v || '—'}</span>
            )} />
            <ComparisonRow label="Type" values={items.map((p) => p.property_type)} ids={items.map((p) => p.id)} />
            <ComparisonRow label="Monthly Rent" values={items.map((p) => p.price)} ids={items.map((p) => p.id)} formatter={formatUSD} highlightMin />
            <ComparisonRow label="Bedrooms" values={items.map((p) => p.bedrooms)} ids={items.map((p) => p.id)} formatter={(v) => (
              <span className="inline-flex items-center gap-1"><Bed className="w-3 h-3" />{v}</span>
            )} highlightMax />
            <ComparisonRow label="Bathrooms" values={items.map((p) => p.bathrooms)} ids={items.map((p) => p.id)} formatter={(v) => (
              <span className="inline-flex items-center gap-1"><Bath className="w-3 h-3" />{v}</span>
            )} highlightMax />
            <ComparisonRow label="Sq ft" values={items.map((p) => p.sqft)} ids={items.map((p) => p.id)} formatter={(v) => (
              <span className="inline-flex items-center gap-1"><Maximize className="w-3 h-3" />{v ? v.toLocaleString() : '—'}</span>
            )} highlightMax />
            <ComparisonRow label="Best Discount" values={items.map((p) => bestDiscount(p))} ids={items.map((p) => p.id)} formatter={(v) => v ? `${v}%` : '—'} highlightMax />
            <ComparisonRow label="Furnished" values={items.map((p) => p.furnished)} ids={items.map((p) => p.id)} formatter={(v) => v
              ? <span className="text-green-600 inline-flex items-center gap-1"><Check className="w-3 h-3" />Yes</span>
              : <span className="text-muted-foreground">No</span>
            } />
            <ComparisonRow label="Pets Allowed" values={items.map((p) => p.pets_allowed)} ids={items.map((p) => p.id)} formatter={(v) => v
              ? <span className="text-green-600 inline-flex items-center gap-1"><Check className="w-3 h-3" />Yes</span>
              : <span className="text-muted-foreground">No</span>
            } />
            <ComparisonRow label="Rating" values={items.map((p) => p.avg_rating)} ids={items.map((p) => p.id)} formatter={(v) => v ? `${Number(v).toFixed(1)} / 5` : '—'} highlightMax />

            {/* CTA row */}
            <div
              className="grid gap-2 sm:gap-3 pt-4"
              style={{ gridTemplateColumns: `clamp(96px, 28vw, 160px) repeat(${cols}, minmax(120px, 1fr))` }}
            >
              <div />
              {items.map((p) => (
                <Button
                  key={p.id}
                  data-testid={`compare-view-${p.id}`}
                  onClick={() => navigate(`/property/${p.id}`)}
                  size="sm"
                  className="rounded-full"
                >
                  View listing
                </Button>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
